import {
  attachSignatureCanvas,
  typedSignatureDataUrl,
} from "../signature_canvas"

/**
 * SignaturePad LiveView hook
 *
 * Inline draw/type signature pad for form-fill signature fields. Pushes the
 * resulting data URL back to the LiveView on a debounce.
 *
 * Data attributes:
 *   data-field-id      - The form field id
 *   data-rf-id         - The request form id
 *   data-initial-value - Previously saved signature data URL
 */
const SignaturePad = {
  mounted() {
    this.fieldId = this.el.dataset.fieldId
    this.rfId = this.el.dataset.rfId
    this.initialValue = this.el.dataset.initialValue || ""
    this.currentValue = this.initialValue
    this.mode = "draw"
    this._buildUI()
    requestAnimationFrame(() => {
      this._initCanvas()
      if (this.pad) this.pad.restore(this.initialValue)
      if (this.initialValue && this.initialValue.startsWith("data:")) {
        this._markSigned()
      }
    })
  },

  destroyed() {
    clearTimeout(this._typeDebounce)
    clearTimeout(this._drawDebounce)
  },

  _buildUI() {
    this.el.innerHTML = `
      <div class="border border-base-300 rounded-lg overflow-hidden bg-base-100">
        <div class="flex border-b border-base-300">
          <button type="button" class="sig-tab flex-1 py-2 text-sm font-semibold bg-base-100 border-r border-base-300 cursor-pointer transition-colors hover:bg-base-200" data-tab="draw">Draw</button>
          <button type="button" class="sig-tab flex-1 py-2 text-sm font-medium text-base-content/50 cursor-pointer transition-colors hover:bg-base-200" data-tab="type">Type</button>
        </div>
        <div data-panel="draw" class="p-2">
          <canvas class="sig-canvas block w-full rounded bg-white" style="height:100px;touch-action:none;cursor:crosshair;"></canvas>
        </div>
        <div data-panel="type" class="p-2" style="display:none">
          <input type="text" class="sig-type-input input input-bordered input-sm w-full mb-2" placeholder="Type your full name" />
          <div class="sig-type-preview flex items-center px-3 rounded bg-white border border-base-200" style="height:60px;font-family:cursive;font-size:32px;color:#111827;"></div>
        </div>
        <div class="flex items-center justify-between px-3 py-2 border-t border-base-200 bg-base-50">
          <button type="button" class="sig-clear btn btn-ghost btn-xs">Clear</button>
          <span class="sig-hint text-xs text-base-content/40">Sign above</span>
        </div>
      </div>
    `

    // Tab switching
    this.el.querySelectorAll(".sig-tab").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.mode = btn.dataset.tab
        this.el.querySelectorAll(".sig-tab").forEach((b) => {
          const active = b.dataset.tab === this.mode
          b.classList.toggle("bg-base-100", active)
          b.classList.toggle("text-base-content/50", !active)
          b.classList.toggle("font-semibold", active)
        })
        this.el.querySelectorAll("[data-panel]").forEach((p) => {
          p.style.display = p.dataset.panel === this.mode ? "block" : "none"
        })
        this._clearSignature()
      })
    })

    // Clear button
    this.el.querySelector(".sig-clear").addEventListener("click", () => {
      this._clearSignature()
    })

    // Type input
    const typeInput = this.el.querySelector(".sig-type-input")
    const typePreview = this.el.querySelector(".sig-type-preview")
    typeInput.addEventListener("input", () => {
      const name = typeInput.value.trim()
      typePreview.textContent = name
      clearTimeout(this._typeDebounce)
      if (name) {
        this._typeDebounce = setTimeout(
          () => this._setValue(typedSignatureDataUrl(name, { minFontSize: 14 })),
          300,
        )
      } else {
        this._setValue("")
      }
    })
  },

  _initCanvas() {
    const canvas = this.el.querySelector(".sig-canvas")
    if (!canvas) return
    this.pad = attachSignatureCanvas(canvas, {
      onStrokeEnd: (dataUrl) => {
        clearTimeout(this._drawDebounce)
        this._drawDebounce = setTimeout(() => this._setValue(dataUrl), 300)
      },
    })
  },

  _clearSignature() {
    if (this.pad) this.pad.clear()
    const typeInput = this.el.querySelector(".sig-type-input")
    const typePreview = this.el.querySelector(".sig-type-preview")
    if (typeInput) typeInput.value = ""
    if (typePreview) typePreview.textContent = ""
    this._setValue("")
  },

  _setValue(dataUrl) {
    this.currentValue = dataUrl
    this.pushEvent("form_change", {
      request_form_id: this.rfId,
      field_id: this.fieldId,
      value: dataUrl,
    })
    this._markSigned(dataUrl)
  },

  _markSigned(dataUrl = true) {
    const hint = this.el.querySelector(".sig-hint")
    if (!hint) return
    hint.textContent = dataUrl ? "✓ Signed" : "Sign above"
    hint.style.color = dataUrl ? "oklch(var(--su, #00a96e))" : ""
  },
}

export default SignaturePad
